/* Shared touch navigation delegates to each brand's existing gallery controls. */
(() => {
  const galleries = [
    ['.n-main-image', '.n-gallery', '.n-thumbs button'],
    ['#afd-root .gallery-image', '.product-gallery', '.gallery-tabs button'],
    ['#outdoor-plus-root .gallery-image', '.product-gallery', '.product-thumbnail-carousel button[aria-pressed], .gallery-tabs button'],
    ['#outdoor-greatroom-root .hero-image', '.gallery', '.gallery-thumbnails button'],
    ['.fm-gallery__main', '.fm-gallery', '[data-fm-thumb]'],
    ['[data-bromic-primary-image]', '.bromic-gallery', '[data-bromic-thumbnail]'],
    ['.n-image-dialog-photo', '.n-image-dialog', null],
    ['.ho-image-dialog-photo', '.ho-image-dialog', null]
  ];
  let gesture = null, suppress = null;
  function resolve(target, swipeOnly = true) {
    for (const [selector, container, buttons] of galleries) {
      const main = target.closest(selector);
      if (!main || main.matches('[data-gallery-zoomed="true"]')) continue;
      const root = main.closest(container);
      if (!root) continue;
      const controls = buttons ? [...root.querySelectorAll(buttons)] : [];
      if (swipeOnly && buttons && controls.length < 2) continue;
      return {main, root, controls};
    }
  }
  document.addEventListener('touchstart', event => {
    gesture = null;
    if (event.touches.length !== 1) return;
    const gallery = resolve(event.target);
    if (!gallery) return;
    const point = event.touches[0];
    gesture = {...gallery, x:point.clientX, y:point.clientY};
  }, {passive:true});
  document.addEventListener('touchmove', event => {
    if (!gesture) return;
    if (event.touches.length !== 1) { gesture = null; return; }
    const dx = event.touches[0].clientX - gesture.x, dy = event.touches[0].clientY - gesture.y;
    if (Math.abs(dy) > 16 && Math.abs(dy) > Math.abs(dx)) { gesture = null; return; }
    if (Math.abs(dx) > 16 && Math.abs(dx) > Math.abs(dy) * 1.5 && event.cancelable) event.preventDefault();
  }, {passive:false});
  document.addEventListener('touchend', event => {
    const g = gesture; gesture = null;
    if (!g || !event.changedTouches.length) return;
    const dx = event.changedTouches[0].clientX - g.x, dy = event.changedTouches[0].clientY - g.y;
    if (Math.abs(dx) < 40 || Math.abs(dx) <= Math.abs(dy) * 1.5) return;
    suppress = {main:g.main, until:performance.now()+600};
    if (!g.controls.length) {
      g.root.querySelector(dx < 0 ? '[aria-label="Next product image"]' : '[aria-label="Previous product image"]')?.click();
      return;
    }
    let index = g.controls.findIndex(b => b.getAttribute('aria-pressed') === 'true' || b.getAttribute('aria-current') === 'true');
    if (index < 0) {
      const src = g.main.querySelector('img')?.getAttribute('src')?.split('?')[0];
      index = g.controls.findIndex(b => b.querySelector('img')?.getAttribute('src')?.split('?')[0] === src);
    }
    g.controls[(Math.max(0,index)+(dx < 0 ? 1 : -1)+g.controls.length)%g.controls.length].click();
  }, {passive:true});
  document.addEventListener('touchcancel', () => gesture = null, {passive:true});
  document.addEventListener('click', event => {
    if (suppress && performance.now() < suppress.until && suppress.main.contains(event.target)) {
      event.preventDefault(); event.stopImmediatePropagation(); suppress = null;
    }
  }, true);

  // A single accessible viewer serves galleries without an existing image dialog.
  let viewer, viewing, returnFocus, oldOverflow;
  function button(label, text, action) {
    const b=document.createElement('button');b.type='button';b.setAttribute('aria-label',label);b.textContent=text;b.addEventListener('click',action);return b;
  }
  function selected(g) {
    let i=g.controls.findIndex(b=>b.getAttribute('aria-pressed')==='true'||b.getAttribute('aria-current')==='true');
    if(i<0){const src=g.main.querySelector('img')?.src?.split('?')[0];i=g.controls.findIndex(b=>b.querySelector('img')?.src?.split('?')[0]===src);}
    return Math.max(0,i);
  }
  function refreshViewer() {
    const source=viewing.main.querySelector('img');if(!source)return;
    viewer.querySelector('.ho-image-dialog-photo img').src=source.src;
    viewer.querySelector('.ho-image-dialog-photo img').alt=source.alt;
    viewer.querySelector('.ho-image-dialog-count').textContent='Image '+(selected(viewing)+1)+' of '+Math.max(1,viewing.controls.length);
    const photo=viewer.querySelector('.ho-image-dialog-photo');photo.dataset.galleryZoomed='false';photo.classList.remove('is-zoomed');photo.scrollTo(0,0);
    const zoom=viewer.querySelector('[data-image-zoom]');zoom.textContent='+';zoom.setAttribute('aria-label','Zoom in');zoom.setAttribute('aria-pressed','false');
    viewer.querySelectorAll('[data-image-step]').forEach(b=>b.disabled=viewing.controls.length<2);
  }
  function step(direction) {
    if(viewing.controls.length<2)return;
    viewing.controls[(selected(viewing)+direction+viewing.controls.length)%viewing.controls.length].click();
    requestAnimationFrame(refreshViewer);
  }
  function openViewer(g, trigger) {
    if(!g.main.querySelector('img'))return;
    if(!viewer){
      viewer=document.createElement('dialog');viewer.className='ho-image-dialog';viewer.setAttribute('aria-label','Product image viewer');
      const top=document.createElement('div');top.className='ho-image-dialog-top';const title=document.createElement('p');title.textContent='Product images';top.append(title,button('Close image viewer','×',()=>viewer.close()));
      const photo=document.createElement('div');photo.className='ho-image-dialog-photo';photo.append(document.createElement('img'));
      const bottom=document.createElement('div');bottom.className='ho-image-dialog-bottom';
      const previous=button('Previous product image','←',()=>step(-1));previous.dataset.imageStep='';
      const next=button('Next product image','→',()=>step(1));next.dataset.imageStep='';
      const zoom=button('Zoom in','+',()=>{const active=photo.dataset.galleryZoomed!=='true';photo.dataset.galleryZoomed=String(active);photo.classList.toggle('is-zoomed',active);zoom.textContent=active?'−':'+';zoom.setAttribute('aria-label',active?'Zoom out':'Zoom in');zoom.setAttribute('aria-pressed',String(active));requestAnimationFrame(()=>photo.scrollTo(active?(photo.scrollWidth-photo.clientWidth)/2:0,active?(photo.scrollHeight-photo.clientHeight)/2:0));});zoom.dataset.imageZoom='';
      const count=document.createElement('p');count.className='ho-image-dialog-count';count.setAttribute('aria-live','polite');bottom.append(previous,zoom,count,next);viewer.append(top,photo,bottom);document.body.append(viewer);
      viewer.addEventListener('close',()=>{document.body.style.overflow=oldOverflow;returnFocus?.focus({preventScroll:true});});
      viewer.addEventListener('keydown',event=>{if(event.key==='ArrowRight'||event.key==='ArrowLeft'){event.preventDefault();step(event.key==='ArrowRight'?1:-1);}});
    }
    viewing=g;returnFocus=trigger;oldOverflow=document.body.style.overflow;refreshViewer();viewer.showModal();document.body.style.overflow='hidden';
  }
  // Included media without a separate product uses the same accessible gallery viewer.
  document.addEventListener('ho:image-view', event => {
    const {src, alt, trigger} = event.detail || {};
    if (!src || viewer?.open) return;
    const main = document.createElement('div');
    const image = document.createElement('img');image.src=src;image.alt=alt || 'Product image';main.append(image);
    openViewer({main, controls:[]}, trigger);
  });
  function addEnlargeControls() {
    for(const [selector] of galleries.slice(1,6)) {
      document.querySelectorAll(selector).forEach(main=>{
        if(!main.querySelector('img')||main.querySelector('.ho-gallery-enlarge'))return;
        const b=button('Enlarge product image','View larger',()=>openViewer(resolve(main,false),b));b.className='ho-gallery-enlarge';main.classList.add('ho-swipe-image');main.append(b);
      });
    }
  }
  // Reuse the shared carousel controller without moving React-owned option rows.
  const optionRows = new Map();
  let rowId = 0;
  function addOptionControls() {
    const template = document.getElementById('ho-option-scroll-template');
    if (!template) return;
    for (const [row, controls] of optionRows) {
      if (!row.isConnected) { controls.remove(); optionRows.delete(row); }
      else if (!controls.isConnected) row.before(controls);
    }
    document.querySelectorAll('#afd-root .accessory-glass-colors, #afd-root .gallery-tabs, #afd-root .lifestyle-thumbnails, .bromic-thumbnails, #outdoor-greatroom-root .gallery-thumbnails').forEach(row => {
      if (optionRows.has(row)) return;
      if (!row.id) row.id = 'ho-option-row-' + (++rowId);
      const controls = template.content.firstElementChild.cloneNode(true);
      controls.dataset.trackId = row.id;
      const label = row.closest('.accessory-glass-group')?.getAttribute('aria-label');
      controls.querySelectorAll('button').forEach(button => {
        button.setAttribute('aria-controls', row.id);
        button.setAttribute('aria-label', (button.hasAttribute('data-carousel-prev') ? 'Previous ' : 'Next ') + (label ? label + ' colors' : 'product images'));
      });
      optionRows.set(row, controls);
      row.before(controls);
    });
  }
  let pending=false;
  new MutationObserver(()=>{if(pending)return;pending=true;requestAnimationFrame(()=>{pending=false;addEnlargeControls();addOptionControls();});}).observe(document.body,{childList:true,subtree:true});
  addEnlargeControls();
  addOptionControls();
})();
